<template>
  <div class="product fixed-nav">
    <MainHeader
      :dark="false"
      :fixedMenu="true"
      :loginButton="true"
      :tiles="false"
      :clipboard="false"
      @logoClick="logoClick"
      v-if="!iframeFullscreen"
    >
      <svg>
        <use v-if="selectedTranslation === 'en'" xlink:href="#logoWmkSvgEng"></use>
        <use v-else xlink:href="#logoWmkSvg"></use>
      </svg>
    </MainHeader>
    <div class="sections">
      <template v-if="productDetails.id">
        <section class="section-main">

          <div class="back-btn-bar">
            <button type="button"
                    class="back-btn"
                    @click.stop="backBtnClick(productDetails.id)">
              <svg height="14" width="24"><use xlink:href="#arrowBackSvg"></use></svg>
              <span class="back-btn-label">
                {{ buttonData.productBack }}
              </span>
            </button>
          </div>

          <template v-if="productGallery.length">
            <SwipeHandler @swipeLeft="swipeLeft" @swipeRight="swipeRight">
              <div class="slider" :class="{'slider-fullscreen': iframeFullscreen}">
                <div class="slide-wrapper" v-if="isLoggedIn">
                  <figure class="slide"
                          v-for="(item, index) in productGallery"
                          :key="'gallery-item-' + index"
                          :class="{
                            'slide-prev': index === (activeItem - 1 + productGallery.length) % productGallery.length,
                            'slide-active': index === activeItem,
                            'slide-next': index === (activeItem + 1) % productGallery.length,
                            'slide-iframe': item.type === '3d'
                          }">
                    <template v-if="item.type === '3d'">
                      <iframe :src="item.url" width="100%"></iframe>
                      <button type="button"
                              :title="(iframeFullscreen ? buttonData.fullscreenClose : buttonData.fullscreenOpen)"
                              class="btn-clean btn-iframe-fullscreen"
                              @click="fullscreenChangeHandler()">
                        <svg v-if="!isMobile">
                          <use v-if="iframeFullscreen" xlink:href="#iconFullScreenClose"></use>
                          <use v-else xlink:href="#iconFullScreenOpen"></use>
                        </svg>
                      </button>
                    </template>
                    <template v-else>
                      <div class="slide-img">
                        <img :src="item.url" :alt="item.alt" />
                      </div>
                    </template>

                    <figcaption v-if="productGallery.length > 1 && !iframeFullscreen">
                      <svg height="16" width="60" v-if="item.type === '3d'">
                        <use xlink:href="#icon360"></use>
                      </svg>
                      <span class="slide-counter">
                        {{(index + 1)}}/{{productGallery.length}}
                      </span>
                    </figcaption>
                  </figure>
                </div>
                <div class="slide-wrapper" v-else>
                  <figure class="slide slide-active">
                    <div class="slide-img">
                      <img :src="productDetails.image" :alt="productDetails.header" />
                    </div>

                    <figcaption v-if="productGallery.length > 1">
                      <span class="slide-counter">
                        1/{{productGallery.length}}
                      </span>
                    </figcaption>
                  </figure>
                </div>
                <SliderControllers
                  class="controllers"
                  @showPrev="showPrev"
                  @showNext="showNext"
                  :hideMobile="false"
                  :small="true"
                  :displayNext="sliderDisplayNext"
                  :displayPrev="sliderDisplayPrev"
                  v-if="productGallery.length > 1 && !iframeFullscreen"
                />
              </div>
            </SwipeHandler>

          </template>
          <template v-else>
            <div class="slider">
              <div class="slide-wrapper">
                <figure class="slide slide-active">
                  <div class="slide-img">
                    <img :src="productDetails.image" :alt="productDetails.header" />
                  </div>
                </figure>
              </div>
            </div>
          </template>

        </section>
        <section class="section-main">
          <div class="container container-small">
            <AccentHeader
              :header="productDetails.header"
              :underline="true"
              :uppercase="false"
            />

            <div class="product-details">
              <ProductParameters
                :parameters="productDetails.parameters"/>
              <ProductDescription
                :header="productDetails.header"
                :description="productDetails.description"
                :status="productDetails.status"
              />
              <FilesDownload
                v-if="isLoggedIn && productDetails['files'].length"
                :files="productDetails.files"
              />
            </div>

          </div>
        </section>
        <section class="section-main product-form" v-if="isLoggedIn">
          <ProductForm :productID="productDetails.id" />
        </section>
        <ProductGallery
          :items="productGalleryItems"
          :maxInit="4"
        />
      </template>
      <template v-else-if="loadingData">
        <section class="section-main container-expand container-expand-404">
          <div class="container container-small text-center">
            <h2 v-accent="productData.single.loading.header"></h2>
            <div class="error-404">
              <p>{{ productData.single.loading.text }}</p>
            </div>
          </div>
        </section>
      </template>
      <template v-else>
        <section class="section-main container-expand container-expand-404 bg-light2">
          <div class="container container-small">
            <AccentHeader
              :header="productData.single.notfound.header + '<br/>' + errorData.pageNotFound.code"
              :underline="true"
              :uppercase="false"
            />
            <div class="error-404">
              <p>{{ productData.single.notfound.text }}
                <router-link :to="{ name: 'search'}">
                  {{ productData.single.notfound.linkLabel }}
                </router-link>
              </p>
            </div>
          </div>
        </section>
      </template>
      <MainFooter
        key="footer"
        :content="footerData"
      />
    </div>
    <section class="modal" v-if="!isLoggedIn && openModal">
      <div class="modal-content">
        <LoginForm addon="true" background="none" />

        <button type="button" class="modal-close btn-close" :title="buttonData.close" @click="toggleModal()">
          <svg>
            <use xlink:href="#closeSvg"></use>
          </svg>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MainHeader from '@/components/MainHeader.vue'
import MainFooter from '@/components/MainFooter.vue'
import ProductParameters from '@/components/product/ProductParameters.vue'
import ProductGallery from '@/components/product/ProductGallery.vue'
import ProductDescription from '@/components/product/ProductDescription.vue'
import FilesDownload from '@/components/product/FilesDownload.vue'
import ProductForm from '@/components/product/ProductForm.vue'
import AccentHeader from '@/components/AccentHeader.vue'
import SliderControllers from '@/components/SliderControllers.vue'
import SwipeHandler from '@/components/SwipeHandler.vue'
import LoginForm from '@/components/LoginForm.vue'
import axios from 'axios'

export default {
  name: 'single-product',
  components: {
    MainHeader,
    MainFooter,
    ProductParameters,
    ProductDescription,
    FilesDownload,
    ProductForm,
    ProductGallery,
    AccentHeader,
    SliderControllers,
    SwipeHandler,
    LoginForm
  },
  data () {
    return {
      activeItem: 0,
      productGallery: [],
      productGalleryItems: [],
      productDetails: [],
      loadingData: true,
      openModal: false,
      sliderDisplayNext: false,
      sliderDisplayPrev: false
    }
  },
  methods: {
    ...mapActions([
      'setActiveListItem',
      'setApiAuthPath',
      'toggleIframeFullscreen',
      'togglePageScrolls'
    ]),
    getProduct (id) {
      axios.get(`${this.apiPath}product/${id}`)
        .then((response) => {
          if (typeof response.data === 'object' && ('id' in response.data)) {
            this.productDetails = response.data
            if (response.data['items'] && response.data['items'].length) {
              this.productGalleryItems = response.data['items']
            }
          } else {
            console.log('Get product / invalid response')
            this.productDetails = []
          }
          this.loadingData = false
        })
        .catch((_err) => {
          console.log('Get product / ' + _err)
          this.productDetails = []
        })
    },
    setProductGallery () {
      let retArr = []

      if (this.productDetails.image3dPath) {
        this.productDetails.image3dPath.forEach(($item) => {
          retArr.push({
            url: $item,
            type: '3d'
          })
        })
      }
      if (this.productDetails['gallery'] && this.productDetails['gallery'].length >= 1) {
        this.productDetails['gallery'].forEach(($item) => {
          retArr.push({
            url: $item.url,
            alt: $item.alt,
            type: 'img'
          })
        })
      } else {
        if (this.productDetails['image']) {
          retArr.push({
            url: this.productDetails['image'],
            alt: this.productDetails.header,
            type: 'img'
          })
        }
      }
      this.productGallery = retArr
      this.sliderItems()
    },
    swipeRight () {
      this.showPrev()
    },
    swipeLeft () {
      this.showNext()
    },
    showNext () {
      if (this.activeItem + 1 < this.productGallery.length) {
        this.activeItem = Math.min(this.activeItem + 1, this.productGallery.length - 1)
      } else {
        this.activeItem = 0
      }

      if (!this.isLoggedIn) {
        this.toggleModal()
      } else {
        this.sliderItems()
      }
    },
    showPrev () {
      if (this.activeItem - 1 >= 0) {
        this.activeItem = Math.max(this.activeItem - 1, 0)
      } else {
        this.activeItem = this.productGallery.length - 1
      }

      if (!this.isLoggedIn) {
        this.toggleModal()
      } else {
        this.sliderItems()
      }
    },
    logoClick () {
      this.$router.push({ 'name': 'search' })
    },
    backBtnClick (id) {
      this.setActiveListItem(id)
      this.$router.go(-1)
    },
    fullscreenChangeHandler () {
      if (!this.isMobile) {
        this.toggleIframeFullscreen()
      }
    },
    toggleModal () {
      this.togglePageScrolls(this.openModal)
      this.openModal = !this.openModal
    },
    sliderItems () {
      if (this.activeItem === 0) {
        this.sliderDisplayPrev = false
        this.sliderDisplayNext = true
      } else if (this.activeItem === this.productGallery.length - 1) {
        this.sliderDisplayPrev = true
        this.sliderDisplayNext = false
      } else {
        this.sliderDisplayPrev = true
        this.sliderDisplayNext = true
      }
    }
  },
  mounted () {
    this.togglePageScrolls(false)
    this.setApiAuthPath()
    this.getProduct(this.$route.params.id)
    this.setProductGallery()
  },
  computed: {
    ...mapGetters([
      'breakpoint',
      'buttonData',
      'errorData',
      'productData',
      'footerData',
      'virtualData',
      'isLoggedIn',
      'isFullscreen',
      'iframeFullscreen',
      'authToken',
      'apiPath',
      'apiAuthPath',
      'selectedTranslation'
    ]),
    isMobile () {
      return this.breakpoint === 'mobile' || this.breakpoint === 'tablet'
    }
  },
  watch: {
    apiPath (oldVal, newVal) {
      if (oldVal !== newVal) {
        this.togglePageScrolls(false)
        this.setApiAuthPath()
        this.getProduct(this.$route.params.id)
        this.setProductGallery()
      }
    },
    productDetails (oldVal, newVal) {
      if (oldVal !== newVal) {
        this.setProductGallery()
      }
    }
  },
  beforeRouteUpdate (to, from, next) {
    this.getProduct(to.params.id)
    next()
  },
  beforeRouteLeave (to, from, next) {
    if (this.iframeFullscreen && !this.isMobile()) {
      this.fullscreenChangeHandler()
    }
    next()
  }
}
</script>

<style scoped lang="scss">
.product-details {

  &-col {
    margin: $gap-md 0 $gap-xl 0;
  } // END .product-details-col;

  @include screen('md') {
    display: flex;
    flex-flow: row nowrap;

    &-col {
      flex: 1 1 auto;
      margin: $gap-xl 0;
      padding: 0 $gap-xl 0 0;
      min-width: 25%;

      &:last-child {
        padding: 0;
      }
    } // END .product-details-col;

    &-params {
      max-width: $product-details-column-left;
    } // END .product-details-params;
    &-downloads {
      max-width: $product-details-column-right;
    } // END .product-details-downloads;
    &-desc {
      max-width: calc(100% - #{($product-details-column-left + $product-details-column-right)});
    } // END .product-details-desc;

  } // END @include screen('md');

} // END .product-details;

.slider {
  margin: $gap-md $gap-md $gap-xl $gap-md;
  overflow: hidden;
  padding: 0 0 ($gap-lg * 2) 0;
  position: relative;

  &-fullscreen {
    background: $body-bg;
    bottom: 0;
    left: 0;
    height: 100%;
    margin: 0;
    padding: 0;
    position: fixed;
    right: 0;
    top: 0;
    width: 100%;
    z-index: 1000;
  } // END .slider-fullscreen;

  @include screen('sm') {
    margin: $gap-md;
    padding: 0;
    max-width: $container-max-width;

    &-fullscreen {
      max-width: 100%;
    }
  }
  @include screen(#{($container-max-width + ($gap-md * 2))}){
    margin: $gap-lg auto;

    &-fullscreen {
      margin: 0;
    }
  }

  .slide {
    bottom: 0;
    display: none;
    left: 0;
    margin: 0;
    height: 100%;
    padding: 0;
    position: absolute;
    text-align: center;
    transition: $transitionSlider;
    width: 100%;
    z-index: 1;

    img {
      height: auto;
      margin: 0 auto;
      width: 100%;
      max-width: $container-content-max-width;

      @include screen('sm') {
        height: 100%;
        width: auto;
      }
    }

    iframe {
      border: 0 none;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &-wrapper {
      height: 0;
      padding-bottom: 100%;
      position: relative;
      width: 100%;

      @include screen('sm'){
        padding-bottom: 52%;
      }
    } // END .slide-wrapper;

    &-img {
      height: 100%;
      overflow: hidden;
      position: relative;
      width: 100%;
    } // END .slide-img;
    &-iframe {
      .btn-iframe-fullscreen {
        bottom: 6px;
        left: 50%;
        margin-left: 72px;
        position: absolute;
        z-index: 1010;
      }
    } // END .slide-iframe
    &-prev,
    &-next,
    &-active {
      display: block;
    }

    &-prev {
      transform: translateX(-100vw);
      opacity: 0;
    }
    &-next {
      transform: translateX(100vw);
      opacity: 0;
      transition: $transitionSlider;
    }
    &-active {
      transform: translateX(0);
      opacity: 1;
    }

    figcaption {
      bottom: -59px;
      display: flex;
      font: {
        size: $font-xs;
        weight: 700;
      }
      position: absolute;
      right: 0;
      width: auto;
      z-index: 100;

      svg {
        height: 16px;
        width: 76px;
      }

      span {
        display: inline-block;
        margin: auto 0 auto $gap-xs;
      }

      @include screen('sm') {
        bottom: $gap-lg;
        right: $gap-md;
      }
    } // END figcaption;

  } // END .slide;

  @include screen('sm'){
    &-fullscreen {
      .slide-wrapper {
        top: 3%;
      }
    }
  }

  .controllers {
    bottom: $gap-sm;
    left: 0;
    position: absolute;
    width: 100px;
    z-index: 10;

    @include screen('sm') {
      bottom: auto;
      left: 1%;
      top: 50%;
      width: 98%;
    }
  }

} // END .slider;

.modal-content {
  max-width: 426px;
}

@include screen('md') {
  .modal-close.btn-close {
    top: ($gap-md * 3);
  }
}
</style>
